import React, { useState, useEffect } from 'react';
import {
  Building2,
  User,
  Mail,
  Phone,
  Globe,
  MapPin,
  CreditCard,
  Briefcase,
  Save,
  X,
  Star,
  Percent
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { usePartnerStore } from '../../lib/stores/partnerStore';
import { usePartnerConfigStore } from '../../lib/stores/partnerConfigStore';
import { useApp } from '../AppProvider';
import type { ExternalPartner, ExternalPartnerFormData } from '../../lib/types';
import { PartnerTagsRow } from './PartnerTagsRow';

interface PartnerFormProps {
  partner?: ExternalPartner;
  onClose: () => void;
  onSuccess?: (partner: ExternalPartner) => void;
}

const STATUS_OPTIONS = [
  { value: 'active', label: 'Attivo' },
  { value: 'inactive', label: 'Inattivo' },
  { value: 'suspended', label: 'Sospeso' },
  { value: 'pending_approval', label: 'In Attesa di Approvazione' }
];

export function PartnerForm({ partner, onClose, onSuccess }: PartnerFormProps) {
  const { createPartner, updatePartner, loading } = usePartnerStore();
  const { 
    partnerTypes, 
    businessSectors, 
    fetchPartnerTypes, 
    fetchBusinessSectors 
  } = usePartnerConfigStore();
  const { toast } = useApp();
  
  // Fetch configurations on mount
  useEffect(() => {
    fetchPartnerTypes();
    fetchBusinessSectors();
  }, []);

  // Il profilo provvigioni (segnalatore) viene creato insieme al partner dallo
  // store: niente piu' interruttore "Abilita provvigioni". La provvigione si
  // imposta qui solo alla creazione, poi dalla scheda (sezione Provvigioni).

  const [formData, setFormData] = useState<ExternalPartnerFormData>({
    name: partner?.name || '', // Changed from company_name to name
    company: partner?.company || '', // Added company field
    email: partner?.email || '',
    phone: partner?.phone || '',
    website: partner?.website || '',
    street_address: partner?.street_address || '',
    city: partner?.city || '',
    postal_code: partner?.postal_code || '',
    country: partner?.country || 'Italia',
    vat_number: partner?.vat_number || '',
    tax_code: partner?.tax_code || '',
    business_sector: partner?.business_sector || '',
    specialization: partner?.specialization || '',
    partner_type: partner?.partner_type || 'service_provider',
    collaboration_start_date: partner?.collaboration_start_date || null,
    commission_rate: partner?.commission_rate || 0,
    hourly_rate: partner?.hourly_rate || 0,
    status: partner?.status || 'active',
    notes: partner?.notes || ''
  });

  const [errors, setErrors] = useState<Partial<ExternalPartnerFormData>>({});

  const validateForm = (): boolean => {
    const newErrors: Partial<ExternalPartnerFormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Il nome del partner è obbligatorio';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Formato email non valido';
    }

    if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
      newErrors.website = 'Formato URL non valido (deve iniziare con http:// o https://)';
    }

    if (formData.commission_rate < 0 || formData.commission_rate > 100) {
      newErrors.commission_rate = 'La percentuale di commissione deve essere tra 0 e 100';
    }

    if (formData.hourly_rate < 0) {
      newErrors.hourly_rate = 'La tariffa oraria non può essere negativa';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      // Clean up form data - convert empty strings to null for date fields
      const cleanedFormData = {
        ...formData,
        collaboration_start_date: formData.collaboration_start_date || null
      };

      let result;
      if (partner) {
        result = await updatePartner(partner.id, cleanedFormData);
      } else {
        result = await createPartner(cleanedFormData);
      }

      if (result) {
        toast.success(partner ? 'Partner aggiornato con successo' : 'Partner creato con successo');
        onSuccess?.(result);
        onClose();
      }
    } catch (error) {
      console.error('Error saving partner:', error);
      toast.error('Errore durante il salvataggio del partner');
    }
  };

  const handleInputChange = (field: keyof ExternalPartnerFormData, value: string | number | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <div className="glass-card rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Building2 className="h-6 w-6 text-black/40 dark:text-white/40" />
          <div>
            <h2 className="text-xl font-semibold text-black dark:text-white">
              {partner ? 'Modifica Partner' : 'Nuovo Partner Esterno'}
            </h2>
            <p className="text-sm text-black/60 dark:text-white/60">
              {partner ? 'Aggiorna le informazioni del partner' : 'Aggiungi un nuovo partner per collaborazioni esterne'}
            </p>
          </div>
        </div>
        <Button variant="ghost" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div>
          <h3 className="text-lg font-medium text-black dark:text-white mb-4">Informazioni Principali</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-black dark:text-white mb-1">
                Nome Partner *
              </label>
              <Input
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Nome del partner/referente"
                className={errors.name ? 'border-red-500' : ''}
              />
              {errors.name && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-black dark:text-white mb-1">
                Azienda
              </label>
              <Input
                value={formData.company}
                onChange={(e) => handleInputChange('company', e.target.value)}
                placeholder="Nome dell'azienda"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-black dark:text-white mb-1">
                Tipo Partner
              </label>
              <select
                value={formData.partner_type}
                onChange={(e) => handleInputChange('partner_type', e.target.value)}
                className="w-full glass-card rounded-lg px-3 py-2 text-black dark:text-white focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent"
              >
                <option value="">Seleziona tipo</option>
                {partnerTypes.filter(pt => pt.is_active).map(type => (
                  <option key={type.id} value={type.name}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-black dark:text-white mb-1">
                Stato
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="w-full glass-card rounded-lg px-3 py-2 text-black dark:text-white focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent"
              >
                {STATUS_OPTIONS.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

          </div>
        </div>

        {/* Tag del profilo segnalatore: solo in modifica (alla creazione il profilo non
            esiste ancora); si salvano subito, non con "Aggiorna Partner" */}
        {partner && (
          <div>
            <h3 className="text-lg font-medium text-black dark:text-white mb-1">Tag</h3>
            <p className="text-xs text-black/50 dark:text-white/50 mb-2">
              Gli stessi tag di lead e clienti, usati anche dalle liste newsletter. Le modifiche si salvano subito.
            </p>
            <PartnerTagsRow partnerId={partner.id} explainMissing />
          </div>
        )}

        {/* Contact Information */}
        <div>
          <h3 className="text-lg font-medium text-black dark:text-white mb-4">Informazioni di Contatto</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-black dark:text-white mb-1">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-black/40 dark:text-white/40" />
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="email@esempio.com"
                  className={`pl-10 ${errors.email ? 'border-red-500' : ''}`}
                />
              </div>
              {errors.email && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-black dark:text-white mb-1">
                Telefono
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-black/40 dark:text-white/40" />
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="+39 123 456 7890"
                  className="pl-10"
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-black dark:text-white mb-1">
                Sito Web
              </label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-black/40 dark:text-white/40" />
                <Input
                  type="url"
                  value={formData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  placeholder="https://www.esempio.com"
                  className={`pl-10 ${errors.website ? 'border-red-500' : ''}`}
                />
              </div>
              {errors.website && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.website}</p>
              )}
            </div>
          </div>
        </div>

        {/* Address Information */}
        <div>
          <h3 className="text-lg font-medium text-black dark:text-white mb-4">Indirizzo</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-black dark:text-white mb-1">
                Indirizzo
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-black/40 dark:text-white/40" />
                <Input
                  value={formData.street_address}
                  onChange={(e) => handleInputChange('street_address', e.target.value)}
                  placeholder="Via Roma, 123"
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-black dark:text-white mb-1">
                Città
              </label>
              <Input
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                placeholder="Milano"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-black dark:text-white mb-1">
                CAP
              </label>
              <Input
                value={formData.postal_code}
                onChange={(e) => handleInputChange('postal_code', e.target.value)}
                placeholder="20100"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-black dark:text-white mb-1">
                Paese
              </label>
              <Input
                value={formData.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                placeholder="Italia"
              />
            </div>
          </div>
        </div>

        {/* Business Information */}
        <div>
          <h3 className="text-lg font-medium text-black dark:text-white mb-4">Informazioni Aziendali</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-black dark:text-white mb-1">
                Partita IVA
              </label>
              <div className="relative">
                <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-black/40 dark:text-white/40" />
                <Input
                  value={formData.vat_number}
                  onChange={(e) => handleInputChange('vat_number', e.target.value)}
                  placeholder="IT12345678901"
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-black dark:text-white mb-1">
                Codice Fiscale
              </label>
              <div className="relative">
                <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-black/40 dark:text-white/40" />
                <Input
                  value={formData.tax_code}
                  onChange={(e) => handleInputChange('tax_code', e.target.value)}
                  placeholder="12345678901"
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-black dark:text-white mb-1">
                Settore di Business
              </label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-black/40 dark:text-white/40" />
                <select
                  value={formData.business_sector}
                  onChange={(e) => handleInputChange('business_sector', e.target.value)}
                  className="w-full glass-card rounded-lg px-3 py-2 pl-10 text-black dark:text-white focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent"
                >
                  <option value="">Seleziona settore</option>
                  {businessSectors.filter(bs => bs.is_active).map(sector => (
                    <option key={sector.id} value={sector.name}>
                      {sector.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-black dark:text-white mb-1">
                Data Inizio Collaborazione
              </label>
              <Input
                type="date"
                value={formData.collaboration_start_date || ''}
                onChange={(e) => handleInputChange('collaboration_start_date', e.target.value || null)}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-black dark:text-white mb-1">
                Specializzazione
              </label>
              <Textarea
                value={formData.specialization}
                onChange={(e) => handleInputChange('specialization', e.target.value)}
                placeholder="Descrivi le competenze e specializzazioni del partner..."
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* Financial Information */}
        <div>
          <h3 className="text-lg font-medium text-black dark:text-white mb-4">Informazioni Finanziarie</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {partner ? (
              <div className="flex items-start gap-2 text-xs text-black/60 dark:text-white/60 md:pt-7">
                <Percent className="h-4 w-4 flex-shrink-0" />
                <span>La provvigione del partner come segnalatore si imposta dalla scheda, sezione Provvigioni.</span>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-black dark:text-white mb-1">
                  Provvigione (%)
                </label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.commission_rate}
                  onChange={(e) => handleInputChange('commission_rate', parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  className={errors.commission_rate ? 'border-red-500' : ''}
                />
                <p className="text-xs text-black/50 dark:text-white/50 mt-1">
                  Sulle offerte in cui il partner è segnalatore. Si può cambiare dalla scheda.
                </p>
                {errors.commission_rate && (
                  <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.commission_rate}</p>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-black dark:text-white mb-1">
                Tariffa Oraria (€)
              </label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formData.hourly_rate}
                onChange={(e) => handleInputChange('hourly_rate', parseFloat(e.target.value) || 0)}
                placeholder="0.00"
                className={errors.hourly_rate ? 'border-red-500' : ''}
              />
              {errors.hourly_rate && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.hourly_rate}</p>
              )}
            </div>
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-black dark:text-white mb-1">
            Note
          </label>
          <Textarea
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            placeholder="Note aggiuntive sul partner..."
            rows={4}
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-6 border-t border-black/10 dark:border-white/10">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Annulla
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="bg-black hover:bg-black/80 text-white dark:bg-white dark:hover:bg-white/80 dark:text-black"
          >
            {loading ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {partner ? 'Aggiorna Partner' : 'Crea Partner'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}